import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Center, Loader, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import {
  ApiError,
  fetchSessionActor,
  fetchStaffVenueProfile,
  type SessionActor,
  type StaffVenueProfile,
} from './api-client.ts';
import { LoginPage } from './pages/LoginPage.tsx';

/**
 * The staff console reads its active venue profile + current actor on boot.
 * `profile.capabilities` drives which panels render (takeout, etc);
 * `profile.venueId` / `profile.timezone` / `profile.branding` replace the
 * previously-hardcoded CONFIG.*.
 *
 * Session actor carries role (`admin` / `manager` / `staff`) used for
 * sidebar + button gating. When the session endpoint is unreachable (local
 * dev without CF Access), we fall back to `admin` so developers aren't
 * locked out of their own dev environment.
 *
 * No synthesised fallback for profile — an unreachable or misconfigured
 * profile endpoint surfaces as an error state rather than silently
 * serving a generic UI (UEEDVP §5).
 */
export interface ProfileWithSession {
  readonly profile: StaffVenueProfile;
  readonly session: SessionActor;
}

const StaffProfileContext = createContext<ProfileWithSession | null>(null);

const DEV_FALLBACK_SESSION: SessionActor = {
  actorId: 'dev-fallback',
  role: 'admin',
  email: null,
};

export function StaffProfileProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<ProfileWithSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unauthenticated, setUnauthenticated] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        // Session is fetched first. 401 → show LoginPage in place.
        let session: SessionActor;
        try {
          session = await fetchSessionActor();
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            setUnauthenticated(true);
            return;
          }
          // Other errors — fall back to dev-admin so local dev stays usable.
          session = DEV_FALLBACK_SESSION;
        }
        const profile = await fetchStaffVenueProfile();
        setState({ profile, session });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  if (unauthenticated) return <LoginPage />;

  useEffect(() => {
    if (state) {
      document.title = `${state.profile.branding.appTitle} · 店家後台`;
    }
  }, [state]);

  if (error) {
    return (
      <Center py="xl" style={{ minHeight: '100vh' }}>
        <Alert icon={<IconAlertCircle size={16} />} title="無法載入場館設定" color="red">
          {error}
        </Alert>
      </Center>
    );
  }

  if (!state) {
    return (
      <Center py="xl" style={{ minHeight: '100vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return <StaffProfileContext.Provider value={state}>{children}</StaffProfileContext.Provider>;
}

export function useStaffProfile(): StaffVenueProfile {
  const ctx = useContext(StaffProfileContext);
  if (!ctx) {
    throw new Error('useStaffProfile must be used inside <StaffProfileProvider />');
  }
  return ctx.profile;
}

export function useSessionActor(): SessionActor {
  const ctx = useContext(StaffProfileContext);
  if (!ctx) {
    throw new Error('useSessionActor must be used inside <StaffProfileProvider />');
  }
  return ctx.session;
}
